import React, { useEffect } from 'react';

const SagaPattern = () => {
  useEffect(() => {
    // SEO optimization
    document.title = 'Saga Pattern - Distributed Transaction Management Guide 2024';
    
    // Add meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Master the Saga Pattern - manage distributed transactions across microservices. Learn choreography vs orchestration with e-commerce and booking examples.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Master the Saga Pattern - manage distributed transactions across microservices. Learn choreography vs orchestration with e-commerce and booking examples.';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    // Add keywords meta tag
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'saga pattern, distributed transactions, microservices, choreography, orchestration, eventual consistency, compensation');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'keywords';
      meta.content = 'saga pattern, distributed transactions, microservices, choreography, orchestration, eventual consistency, compensation';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    return () => {
      // Cleanup - restore original title
      document.title = 'SlazySloth - Deep Dives';
    };
  }, []);

  const styles = {
    container: {
      width: '100%',
      margin: 0,
      padding: '2rem',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
      lineHeight: 1.6,
      color: '#333',
      background: '#ffffff',
      fontSize: '16px'
    },
    header: {
      marginBottom: '2rem'
    },
    title: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      marginBottom: '1rem'
    },
    subtitle: {
      fontSize: '16px',
      color: '#333',
      marginBottom: '1rem',
      fontWeight: 400
    },
    content: {
      fontSize: '16px',
      lineHeight: 1.6
    },
    heading: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      margin: '1.5rem 0 1rem 0'
    },
    paragraph: {
      marginBottom: '1rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    list: {
      marginBottom: '1rem',
      paddingLeft: '1.5rem'
    },
    listItem: {
      marginBottom: '0.5rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    code: {
      background: '#f5f5f5',
      padding: '0.2rem 0.4rem',
      fontFamily: 'monospace',
      fontSize: '16px',
      color: '#333'
    },
    codeBlock: {
      background: '#f5f5f5',
      padding: '1rem',
      fontFamily: 'monospace',
      fontSize: '14px',
      color: '#333',
      marginBottom: '1rem',
      whiteSpace: 'pre-wrap',
      overflow: 'auto'
    },
    blockquote: {
      borderLeft: '4px solid #008080',
      paddingLeft: '1rem',
      margin: '1rem 0',
      fontStyle: 'italic',
      color: '#555'
    }
  };

  return (
    <article style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.title}>Saga Pattern</h1>
        <p style={styles.subtitle}>
          Distributed Transaction Management in Microservices Architecture
        </p>
      </header>

      <main style={styles.content}>
        <section>
          <h2 style={styles.heading}>What is the Saga Pattern?</h2>
          <p style={styles.paragraph}>
            The Saga pattern is a design pattern for managing distributed transactions across multiple 
            microservices. Instead of using traditional ACID transactions that lock resources, sagas 
            break down a transaction into a series of smaller, independent transactions that can be 
            rolled back using compensating actions if something goes wrong.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Why Do We Need Sagas?</h2>
          <p style={styles.paragraph}>
            In monolithic applications, we can use database transactions to ensure data consistency. 
            However, in microservices architecture, each service has its own database, making 
            traditional ACID transactions impossible across service boundaries. Sagas provide a way 
            to maintain data consistency without distributed locks.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Types of Saga Implementation</h2>
          
          <h3 style={styles.heading}>1. Choreography-Based Saga</h3>
          <p style={styles.paragraph}>
            In choreography, each service publishes events and listens to events from other services. 
            There's no central coordinator - services coordinate themselves.
          </p>

          <div style={styles.codeBlock}>
{`// E-commerce Order Processing - Choreography Example

// Order Service
class OrderService {
    async createOrder(orderData) {
        try {
            // 1. Create order in pending state
            const order = await this.orderRepository.create({
                ...orderData,
                status: 'PENDING',
                sagaId: generateSagaId()
            });
            
            // 2. Publish OrderCreated event
            await this.eventBus.publish('OrderCreated', {
                sagaId: order.sagaId,
                orderId: order.id,
                customerId: order.customerId,
                items: order.items,
                totalAmount: order.totalAmount
            });
            
            return order;
        } catch (error) {
            console.error('Order creation failed:', error);
            throw error;
        }
    }
    
    // Handle payment success
    async handlePaymentProcessed(event) {
        const { sagaId, orderId, paymentId } = event;
        
        await this.orderRepository.update(orderId, {
            status: 'PAYMENT_CONFIRMED',
            paymentId: paymentId
        });
        
    }
    
    // Handle inventory reservation success
    async handleInventoryReserved(event) {
        const { sagaId, orderId } = event;
        
        await this.orderRepository.update(orderId, {
            status: 'INVENTORY_RESERVED'
        });
        
        // Publish event to trigger shipping
        await this.eventBus.publish('ReadyForShipping', {
            sagaId,
            orderId
        });
    }
    
    // Handle compensation events
    async handlePaymentFailed(event) {
        const { sagaId, orderId, reason } = event;
        
        await this.orderRepository.update(orderId, {
            status: 'CANCELLED',
            cancellationReason: reason
        });
        
    }
}

// Payment Service
class PaymentService {
    constructor() {
        this.eventBus.subscribe('OrderCreated', this.processPayment.bind(this));
    }
    
    async processPayment(event) {
        const { sagaId, orderId, customerId, totalAmount } = event;
        
        try {
            // Simulate payment processing
            const payment = await this.paymentGateway.charge({
                customerId,
                amount: totalAmount,
                orderId
            });
            
            if (payment.success) {
                // Payment successful - publish success event
                await this.eventBus.publish('PaymentProcessed', {
                    sagaId,
                    orderId,
                    paymentId: payment.id,
                    amount: totalAmount
                });
            } else {
                throw new Error('Payment declined');
            }
            
        } catch (error) {
            // Payment failed - publish failure event
            await this.eventBus.publish('PaymentFailed', {
                sagaId,
                orderId,
                reason: error.message
            });
        }
    }
    
    // Compensation action
    async refundPayment(event) {
        const { sagaId, orderId, paymentId } = event;
        
        try {
            await this.paymentGateway.refund(paymentId);
        } catch (error) {
            console.error('Refund failed:', error);
            // In real scenario, might need manual intervention
        }
    }
}

// Inventory Service
class InventoryService {
    constructor() {
        this.eventBus.subscribe('PaymentProcessed', this.reserveInventory.bind(this));
        this.eventBus.subscribe('OrderCancelled', this.releaseInventory.bind(this));
    }
    
    async reserveInventory(event) {
        const { sagaId, orderId, items } = event;
        
        try {
            // Check and reserve inventory
            for (const item of items) {
                const available = await this.checkAvailability(item.productId, item.quantity);
                if (!available) {
                    throw new Error(\`Insufficient inventory for product \${item.productId}\`);
                }
            }
            
            // Reserve all items
            const reservationId = await this.reserveItems(items);
            
            // Publish success event
            await this.eventBus.publish('InventoryReserved', {
                sagaId,
                orderId,
                reservationId,
                items
            });
            
        } catch (error) {
            // Inventory reservation failed - trigger compensation
            await this.eventBus.publish('InventoryReservationFailed', {
                sagaId,
                orderId,
                reason: error.message
            });
            
            // Trigger payment refund
            await this.eventBus.publish('RefundPayment', {
                sagaId,
                orderId
            });
        }
    }
    
    async releaseInventory(event) {
        const { sagaId, orderId, reservationId } = event;
        
        try {
            await this.releaseReservation(reservationId);
        } catch (error) {
            console.error('Failed to release inventory:', error);
        }
    }
}

// Shipping Service
class ShippingService {
    constructor() {
        this.eventBus.subscribe('ReadyForShipping', this.createShipment.bind(this));
    }
    
    async createShipment(event) {
        const { sagaId, orderId } = event;
        
        try {
            const shipment = await this.createShippingLabel(orderId);
            
            await this.eventBus.publish('ShipmentCreated', {
                sagaId,
                orderId,
                shipmentId: shipment.id,
                trackingNumber: shipment.trackingNumber
            });
            
            
        } catch (error) {
            await this.eventBus.publish('ShippingFailed', {
                sagaId,
                orderId,
                reason: error.message
            });
        }
    }
}`}
          </div>

          <h3 style={styles.heading}>2. Orchestration-Based Saga</h3>
          <p style={styles.paragraph}>
            In orchestration, a central coordinator (orchestrator) manages the saga workflow, 
            telling each service what to do and when.
          </p>

          <div style={styles.codeBlock}>
{`// E-commerce Order Processing - Orchestration Example

class OrderSagaOrchestrator {
    constructor(orderService, paymentService, inventoryService, shippingService) {
        this.orderService = orderService;
        this.paymentService = paymentService;
        this.inventoryService = inventoryService;
        this.shippingService = shippingService;
        this.sagaStates = new Map(); // Store saga state
    }
    
    async startOrderSaga(orderData) {
        const sagaId = generateSagaId();
        
        // Initialize saga state
        this.sagaStates.set(sagaId, {
            id: sagaId,
            status: 'STARTED',
            currentStep: 'CREATE_ORDER',
            orderData,
            compensationActions: []
        });
        
        try {
            await this.executeOrderSaga(sagaId);
        } catch (error) {
            await this.compensate(sagaId);
            throw error;
        }
    }
    
    async executeOrderSaga(sagaId) {
        const saga = this.sagaStates.get(sagaId);
        
        try {
            // Step 1: Create Order
            saga.currentStep = 'CREATE_ORDER';
            const order = await this.orderService.createOrder(saga.orderData);
            saga.orderId = order.id;
            saga.compensationActions.push({
                action: 'CANCEL_ORDER',
                data: { orderId: order.id }
            });
            
            // Step 2: Process Payment
            saga.currentStep = 'PROCESS_PAYMENT';
            const payment = await this.paymentService.processPayment({
                customerId: saga.orderData.customerId,
                amount: saga.orderData.totalAmount,
                orderId: order.id
            });
            saga.paymentId = payment.id;
            saga.compensationActions.push({
                action: 'REFUND_PAYMENT',
                data: { paymentId: payment.id }
            });
            
            // Step 3: Reserve Inventory
            saga.currentStep = 'RESERVE_INVENTORY';
            const reservation = await this.inventoryService.reserveInventory({
                orderId: order.id,
                items: saga.orderData.items
            });
            saga.reservationId = reservation.id;
            saga.compensationActions.push({
                action: 'RELEASE_INVENTORY',
                data: { reservationId: reservation.id }
            });
            
            // Step 4: Create Shipment
            saga.currentStep = 'CREATE_SHIPMENT';
            const shipment = await this.shippingService.createShipment({
                orderId: order.id,
                shippingAddress: saga.orderData.shippingAddress
            });
            saga.shipmentId = shipment.id;
            
            // Step 5: Complete Order
            saga.currentStep = 'COMPLETE_ORDER';
            await this.orderService.completeOrder(order.id, {
                paymentId: payment.id,
                reservationId: reservation.id,
                shipmentId: shipment.id
            });
            
            saga.status = 'COMPLETED';
            
        } catch (error) {
            saga.status = 'FAILED';
            saga.error = error.message;
            console.error(\`Order saga \${sagaId} failed at step \${saga.currentStep}:, error\`);
            throw error;
        }
    }
    
    async compensate(sagaId) {
        const saga = this.sagaStates.get(sagaId);
        
        // Execute compensation actions in reverse order
        const compensationActions = [...saga.compensationActions].reverse();
        
        for (const compensation of compensationActions) {
            try {
                await this.executeCompensation(compensation);
            } catch (error) {
                console.error(\`Compensation action \${compensation.action} failed:, error\`);
                // In production, you might want to retry or alert operations team
            }
        }
        
        saga.status = 'COMPENSATED';
    }
    
    async executeCompensation(compensation) {
        switch (compensation.action) {
            case 'CANCEL_ORDER':
                await this.orderService.cancelOrder(compensation.data.orderId);
                break;
                
            case 'REFUND_PAYMENT':
                await this.paymentService.refundPayment(compensation.data.paymentId);
                break;
                
            case 'RELEASE_INVENTORY':
                await this.inventoryService.releaseReservation(compensation.data.reservationId);
                break;
                
            default:
        }
    }
    
    // Get saga status
    getSagaStatus(sagaId) {
        return this.sagaStates.get(sagaId);
    }
    
    // Cleanup completed sagas (in production, you'd persist this)
    cleanupCompletedSagas() {
        for (const [sagaId, saga] of this.sagaStates) {
            if (saga.status === 'COMPLETED' || saga.status === 'COMPENSATED') {
                this.sagaStates.delete(sagaId);
            }
        }
    }
}

// Usage Example
async function processOrder() {
    const orchestrator = new OrderSagaOrchestrator(
        orderService,
        paymentService,
        inventoryService,
        shippingService
    );
    
    const orderData = {
        customerId: 'customer-123',
        items: [
            { productId: 'product-1', quantity: 2, price: 29.99 },
            { productId: 'product-2', quantity: 1, price: 49.99 }
        ],
        totalAmount: 109.97,
        shippingAddress: {
            street: '123 Main St',
            city: 'Anytown',
            zipCode: '12345'
        }
    };
    
    try {
        await orchestrator.startOrderSaga(orderData);
    } catch (error) {
        console.error('Order processing failed:', error);
    }
}`}
          </div>
        </section>

        <section>
          <h2 style={styles.heading}>Choreography vs Orchestration</h2>
          
          <h3 style={styles.heading}>Choreography Pros & Cons</h3>
          <div style={styles.paragraph}>
            <strong>Pros:</strong>
            <ul style={styles.list}>
              <li style={styles.listItem}>Decentralized - no single point of failure</li>
              <li style={styles.listItem}>Services are loosely coupled</li>
              <li style={styles.listItem}>Better scalability</li>
              <li style={styles.listItem}>Follows microservices principles</li>
            </ul>
            
            <strong>Cons:</strong>
            <ul style={styles.list}>
              <li style={styles.listItem}>Complex to monitor and debug</li>
              <li style={styles.listItem}>Difficult to understand the overall flow</li>
              <li style={styles.listItem}>Harder to handle complex business logic</li>
              <li style={styles.listItem}>Can lead to cyclic dependencies</li>
            </ul>
          </div>

          <h3 style={styles.heading}>Orchestration Pros & Cons</h3>
          <div style={styles.paragraph}>
            <strong>Pros:</strong>
            <ul style={styles.list}>
              <li style={styles.listItem}>Centralized control and monitoring</li>
              <li style={styles.listItem}>Easier to understand and debug</li>
              <li style={styles.listItem}>Better handling of complex workflows</li>
              <li style={styles.listItem}>Explicit error handling and compensation</li>
            </ul>
            
            <strong>Cons:</strong>
            <ul style={styles.list}>
              <li style={styles.listItem}>Central orchestrator is a single point of failure</li>
              <li style={styles.listItem}>Can become a bottleneck</li>
              <li style={styles.listItem}>Creates coupling between orchestrator and services</li>
              <li style={styles.listItem}>More complex to scale</li>
            </ul>
          </div>
        </section>

        <section>
          <h2 style={styles.heading}>Best Practices</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>Idempotency:</strong> Ensure all operations are idempotent to handle retries safely</li>
            <li style={styles.listItem}><strong>Timeouts:</strong> Set appropriate timeouts for each step</li>
            <li style={styles.listItem}><strong>Monitoring:</strong> Implement comprehensive logging and monitoring</li>
            <li style={styles.listItem}><strong>State Persistence:</strong> Persist saga state for recovery</li>
            <li style={styles.listItem}><strong>Compensation Design:</strong> Design compensation actions carefully</li>
            <li style={styles.listItem}><strong>Event Ordering:</strong> Handle out-of-order events gracefully</li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>When to Use Sagas</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}>Microservices architecture with distributed data</li>
            <li style={styles.listItem}>Long-running business processes</li>
            <li style={styles.listItem}>When ACID transactions across services are not feasible</li>
            <li style={styles.listItem}>When eventual consistency is acceptable</li>
            <li style={styles.listItem}>Complex workflows with multiple services</li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>Conclusion</h2>
          <p style={styles.paragraph}>
            The Saga pattern is essential for managing distributed transactions in microservices 
            architecture. While it adds complexity, it provides a way to maintain data consistency 
            across service boundaries without the limitations of distributed ACID transactions. 
            Choose choreography for simple workflows and orchestration for complex business processes.
          </p>
        </section>
      </main>
    </article>
  );
};

export default SagaPattern;
